import * as amplitude from '@amplitude/analytics-browser';
import type { SessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';
import { Address } from 'viem';

import { logger } from '@/lib/logger';

import { ampli, ApiKey } from '../ampli';
import { NETWORK_NAME_FOR_URLPATH_PER_CHAIN_ID } from '../constants/chain';
import { ENV } from '../environment/client';
import { ChainAddress } from './models';

type TxnModuleEventTokenParam = {
  address: ChainAddress;
  chainId: number;
  symbol: string;
};

/**
 * Returns a token pair identifier for the transaction module event.
 *
 * @param sellToken - Sell token info
 * @param buyToken - Buy token info
 */
export const getTokenPair = (
  sellToken: TxnModuleEventTokenParam | undefined,
  buyToken: TxnModuleEventTokenParam | undefined,
) => {
  if (!sellToken || !buyToken) return undefined;

  return sellToken.symbol >= buyToken.symbol
    ? `${NETWORK_NAME_FOR_URLPATH_PER_CHAIN_ID[sellToken.chainId]}-${sellToken.symbol}-${
        NETWORK_NAME_FOR_URLPATH_PER_CHAIN_ID[buyToken.chainId]
      }-${buyToken.symbol}`
    : `${NETWORK_NAME_FOR_URLPATH_PER_CHAIN_ID[buyToken.chainId]}-${buyToken.symbol}-${
        NETWORK_NAME_FOR_URLPATH_PER_CHAIN_ID[sellToken.chainId]
      }-${sellToken.symbol}`;
};

const bucketLabels = [
  '0-10',
  '10-100',
  '100-1000',
  '1000-10000',
  '10000-100000',
  '100000-1000000',
  '1000000+',
];
/**
 * Returns a bucket for the USD dollar amount for the transaction module event.
 * This is a special ask from Data team to bucketize the USD dollar amount in amplitude.
 **/
export function getUsdDollarAmountBucket(usdDollarAmount: number): string {
  if (usdDollarAmount <= 10) return '0-10';
  const { floor, log10, min } = Math;
  const bucketIndex = min(floor(log10(usdDollarAmount)), 6); // cap the index at 6 for $1000000+ bucket

  return bucketLabels[bucketIndex];
}

/**
 * Custom User Properties - Set of addresses used and networks connected to per device ID.
 */
export enum USER_PROPERTIES {
  WALLET_ADDRESSES_CONNECTED = 'wallet_addresses_connected',
  CHAIN_ID = 'chain_id',
  CHAIN_NAME = 'chain_name',
  EVM_WALLET_ADDRESS = 'evm_wallet_address',
  EVM_WALLET_PROVIDER = 'evm_wallet_provider',
  SOLANA_WALLET_ADDRESS = 'solana_wallet_address',
  SOLANA_WALLET_PROVIDER = 'solana_wallet_provider',
}

/**
 * Identify Types
 * https://www.docs.developers.amplitude.com/data/sdks/typescript-browser/#user-properties
 */
export enum IDENTIFY_TYPES {
  SET = 'set',
  ADD = 'add',
  POST_INSERT = 'postInsert',
}

/**
 * Search Types
 */
export enum SEARCH_TYPES {
  HOME_PAGE = 'home_page',
  HEADER = 'header',
  SELL_TOKEN = 'sell_token',
  BUY_TOKEN = 'buy_token',
}

// Holds the registered session replay plugin instance once initializeAmpli runs.
let sessionReplayInstance: SessionReplayPlugin | undefined;

export const initializeAmpli = () => {
  let apiKey = ApiKey.production;

  // Ampli migration override. When enabled, always route to migration project key.
  if (
    ENV.NEXT_PUBLIC_AMPLITUDE_MODE === 'migration' &&
    ENV.NEXT_PUBLIC_AMPLITUDE_API_KEY_MIGRATION
  ) {
    apiKey = ENV.NEXT_PUBLIC_AMPLITUDE_API_KEY_MIGRATION;
  } else if (
    (process.env.NODE_ENV === 'development' ||
      ENV.NEXT_PUBLIC_VERCEL_ENV === 'preview' ||
      ENV.NEXT_PUBLIC_VERCEL_ENV === 'development') &&
    ENV.NEXT_PUBLIC_AMPLITUDE_API_KEY
  ) {
    apiKey = ENV.NEXT_PUBLIC_AMPLITUDE_API_KEY;
  }

  if (!apiKey) {
    logger.warn('Amplitude API key not found. Events will not be logged.');
    return;
  }

  // Same-site Cloudflare Worker (t.matcha.xyz) fronting the Amplitude ingest
  // endpoints: keeps telemetry bytes off Vercel and away from ad-blockers that
  // filter amplitude.com. The worker's CORS allowlist covers the production
  // hostnames and *-0x-eng.vercel.app previews; local dev falls back to the
  // SDK's amplitude.com endpoints so it works without the allowlist.
  const proxyOrigin =
    ENV.NEXT_PUBLIC_VERCEL_ENV === 'production' || ENV.NEXT_PUBLIC_VERCEL_ENV === 'preview'
      ? 'https://t.matcha.xyz'
      : undefined;

  ampli.load({
    client: {
      apiKey,
      configuration: {
        ...(proxyOrigin ? { serverUrl: `${proxyOrigin}/api/report` } : {}),
        defaultTracking: {
          attribution: true,
          pageViews: { trackHistoryChanges: 'all' },
          sessions: true,
          formInteractions: false,
          fileDownloads: false,
        },
        flushIntervalMillis: 3000,
      },
    },
  });

  // Keep session replay attached through the Ampli-managed client.
  // Retain the instance so we can read the replay id later (the client exposes no
  // getter to look up a plugin by name).
  //
  // The plugin (~rrweb DOM recorder) is loaded dynamically so it stays out of the
  // shared entry chunk; events fired before the chunk resolves simply lack the
  // replay id property, which `getSessionReplayId()` already treats as
  // not-recording.
  import('@amplitude/plugin-session-replay-browser')
    .then(({ sessionReplayPlugin }) => {
      sessionReplayInstance = sessionReplayPlugin({
        // The SDK default is now `memory`, which drops buffered events on reload
        // or tab close. Keep IndexedDB; the SDK falls back to memory by itself
        // when storage is unavailable or fails.
        storeType: 'idb',
        ...(proxyOrigin
          ? {
              configServerUrl: `${proxyOrigin}/api/sr/config`,
              trackServerUrl: `${proxyOrigin}/api/sr/track`,
            }
          : {}),
      }) as unknown as SessionReplayPlugin;
      ampli.client.add(sessionReplayInstance);
    })
    .catch((error) => {
      logger.warn('Failed to load session replay plugin:', error);
    });
};

// Amplitude tags replayed sessions with this event property; its value is the
// session replay id (`<deviceId>/<sessionId>`).
const SESSION_REPLAY_ID_PROPERTY = '[Amplitude] Session Replay ID';

/**
 * Deep-link base for an Amplitude session replay. Append a session replay id
 * (`<deviceId>/<sessionId>`) to open the recording. Shared by every consumer
 * (Pylon support context, Sentry error context) so the link is defined once.
 *
 * The `/` separating device and session id must stay unencoded for Amplitude to resolve it.
 */
export const AMPLITUDE_SESSION_REPLAY_URL_BASE =
  'https://app.amplitude.com/analytics/0x/session-replay/project/427066/search/replay?sessionReplayId=';

/** Builds the Amplitude session replay deep link for a given replay id. */
export function getSessionReplayUrl(replayId: string): string {
  return `${AMPLITUDE_SESSION_REPLAY_URL_BASE}${replayId}`;
}

/**
 * Returns the current Amplitude session replay id (`<deviceId>/<sessionId>`), or
 * `undefined` when the session is not being recorded (e.g. not selected by the
 * sample rate) or session replay has not been initialised.
 *
 * `getSessionReplayProperties()` returns an empty object when the session is not
 * recording, so an absent/non-string value maps to `undefined`.
 */
export function getSessionReplayId(): string | undefined {
  const properties = sessionReplayInstance?.getSessionReplayProperties();
  const replayId = properties?.[SESSION_REPLAY_ID_PROPERTY];
  return typeof replayId === 'string' && replayId.length > 0 ? replayId : undefined;
}

/**
 * Amplitude identifiers for correlating a Sentry error with the user's Amplitude
 * activity and session replay. Every field is optional because Amplitude may not
 * be initialised yet (early errors, SSR) or the session may not be recorded.
 */
export type AmplitudeErrorContext = {
  /** Amplitude device id, stable across sessions on a device. */
  deviceId?: string;
  /** Amplitude session id (epoch ms) for the current session. */
  sessionId?: number;
  /** Session replay id (`<deviceId>/<sessionId>`); present only while recording. */
  sessionReplayId?: string;
  /** Clickable Amplitude session replay deep link; present only while recording. */
  sessionReplayUrl?: string;
};

/**
 * Reads the Amplitude identifiers used to link a Sentry error back to Amplitude.
 *
 * Safe to call before Amplitude has loaded: the SDK getters return `undefined`
 * off the main client, and `getSessionReplayId()` already guards the not-recording
 * case. The replay url is only set when a replay id exists.
 */
export function getAmplitudeErrorContext(): AmplitudeErrorContext {
  const sessionReplayId = getSessionReplayId();
  return {
    deviceId: amplitude.getDeviceId(),
    sessionId: amplitude.getSessionId(),
    sessionReplayId,
    sessionReplayUrl: sessionReplayId ? getSessionReplayUrl(sessionReplayId) : undefined,
  };
}

export const identifyTheme = () => {
  const [dark, light] = ['dark', 'light'];
  const theme = document.body.dataset['theme'];

  if (!theme) logger.warn('Theme not set.');

  if (theme === light) {
    identifyUserProperties({
      type: IDENTIFY_TYPES.SET,
      key: 'theme_toggle_position',
      value: light,
    });
  } else if (theme === dark) {
    identifyUserProperties({
      type: IDENTIFY_TYPES.SET,
      key: 'theme_toggle_position',
      value: dark,
    });
  } else if (theme === 'system') {
    identifyUserProperties({
      type: IDENTIFY_TYPES.SET,
      key: 'theme_toggle_position',
      value: window.matchMedia('(prefers-color-scheme: dark)').matches ? dark : light,
    });
  }
};

export const identifyUserProperties = ({
  type,
  key,
  value = '',
}: {
  type: IDENTIFY_TYPES;
  key: string;
  value?: string | Address;
}) => {
  const identifyObj = new amplitude.Identify();
  if (type === IDENTIFY_TYPES.ADD) {
    identifyObj.add(key, parseFloat(value));
  } else {
    identifyObj[type](key, value);
  }
  ampli.client.identify(identifyObj);
  return;
};
